// ÁTTEKINTÉS (kezdőlap) — JOG-VEZÉRELT és TESTRESZABHATÓ csempe-készlet: mindenki azt látja, amivel dolgozik, és úgy
// rendezheti, ahogy neki jó (az elrendezés a SZERVEREN tárolódik, eszközről eszközre követi).
//
// A LAP SZERKEZETE:
//   (a) fix fejléc: köszöntés (NINCS alcím) · „Új…" menü · „Testreszabás",
//   (b) fix blokk: „Rám vár" (teljes szélesség, ha van sor-jog; a kitűzött közlemény KÖTELEZŐ csempe a rácsban),
//   (c) a testreszabható csempe-terület: a felhasználó rendezett listája (`resolveLayout`),
//       egy folytonos 12 oszlopos rácsban (`layout/spans.ts`: kicsi/közepes/széles, `row dense`; mobilon egy oszlop,
//       lista-sorrendben).
//
// A JOG-MODELL: a csempe-katalógus (kapu, méret, alap-sorrend) a `@atlas/shared`-ben él, a `resolveLayout` csak a
// jogosult csempéket adja; a lekérdezés pedig csak akkor fut, ha a csempe ki van rakva ÉS jogosult — jog nélkül NINCS
// csempe és NINCS hálózati hívás sem. A jog nélküli, de mentett csempe a mentett listában megmarad (jog-visszaadáskor
// visszajön a helyére).
//
// AMI SZÁNDÉKOSAN NINCS ITT: „Legutóbbi események" (az audit külön lapon él) · „Legutóbbi közlemény" (a harang szól róla).
//
// IGAZÍTÁS: `PageBody align="left"` — a tartalom a topbar-cím vonalán kezdődik.
import { addressName, type DashboardTileEntry, type DashboardWindowDays } from '@atlas/shared';
import { AdjustmentsHorizontalIcon } from '@heroicons/react/24/outline';
import { createFileRoute, useBlocker, useNavigate } from '@tanstack/react-router';
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { AppShell } from '@/components/shared/app-shell';
import { ConfirmDialog } from '@/components/shared/confirm-dialog';
import { PageBody } from '@/components/shared/page-body';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';
import { DashboardEditor } from '@/features/dashboard/components/dashboard-editor';
import { DashboardTiles } from '@/features/dashboard/components/dashboard-tiles';
import {
  EditorBottomBar,
  EditorHeaderActions,
} from '@/features/dashboard/components/editor-toolbar';
import { MyWorkCard } from '@/features/dashboard/components/my-work-card';
import { newActions, newActionsFab } from '@/features/dashboard/components/new-actions';
import { NewMenu } from '@/features/dashboard/components/new-menu';
import { TileGallerySheet } from '@/features/dashboard/components/tile-gallery-sheet';
import { DASHBOARD_GRID_CLASS, tileSpanClass } from '@/features/dashboard/layout/spans';
import type { TileContext } from '@/features/dashboard/tiles/registry';
import { dashboardPerms, useDashboardData } from '@/features/dashboard/use-dashboard-data';
import { useDashboardEditor } from '@/features/dashboard/use-dashboard-editor';
import { useDashboardLayout } from '@/features/dashboard/use-dashboard-layout';
import { api } from '@/lib/api';
import { requireAuth } from '@/lib/auth';
import { cn } from '@/lib/utils';
import { EntityHttpError } from '@/lib/use-entity-mutation';
import { usePermissions } from '@/lib/use-permissions';

export const Route = createFileRoute('/')({
  beforeLoad: () => requireAuth(),
  component: Dashboard,
});

/** Napszak-köszöntés kulcs-töve a helyi óra szerint (reggel/délután/este). */
function greetingKey(hour: number): 'morning' | 'afternoon' | 'evening' {
  if (hour < 12) return 'morning';
  if (hour < 18) return 'afternoon';
  return 'evening';
}

export function Dashboard(): JSX.Element {
  const { t } = useTranslation();
  const { can } = usePermissions();
  const navigate = useNavigate();
  const session = api.session.useQuery(['session']);

  const profile = session.data?.status === 200 ? session.data.body : undefined;
  // MEGSZÓLÍTÁS: a becenév, különben a keresztnév (a közös szabály — `@atlas/shared` `addressName`)
  const firstName =
    (profile && addressName({ nickname: profile.nickname, givenName: profile.givenName })) ??
    profile?.name ??
    '';
  const currentUserId = profile?.sub ?? null;
  // MEGSZEMÉLYESÍTÉS alatt az Odin az írást tiltja → a testreszabás nem érhető el
  const impersonating = Boolean(profile?.impersonation);

  const key = greetingKey(new Date().getHours());
  const greeting = firstName
    ? t(`dashboard.greeting.${key}Named`, { name: firstName })
    : t(`dashboard.greeting.${key}`);

  // ── elrendezés ─────────────────────────────────────────────────────────────────────────────────
  const layoutHandle = useDashboardLayout();
  const { layout } = layoutHandle;
  const entries = useMemo<DashboardTileEntry[]>(
    () =>
      layout.tiles.map((tile) => ({
        id: tile.def.id,
        size: tile.size,
        ...(tile.opts ? { opts: tile.opts } : {}),
      })),
    [layout.tiles],
  );
  // A KIRAKOTT csempék: betöltés alatt üres (ne induljon lekérdezés egy még nem ismert elrendezésre)
  const placed = useMemo(
    () => new Set(layoutHandle.isLoading ? [] : layout.tiles.map((tile) => tile.def.id)),
    [layout.tiles, layoutHandle.isLoading],
  );

  const perms = dashboardPerms(can);
  const data = useDashboardData(perms, placed);
  const showCounts = placed.has('kpi.clients') || placed.has('kpi.accounts');
  const dash = api.dashboard.useQuery(['dashboard'], undefined, {
    enabled: showCounts,
    queryKey: ['dashboard'],
  });
  const body = dash.data?.status === 200 ? dash.data.body : undefined;

  // ── szerkesztő-mód ─────────────────────────────────────────────────────────────────────────────
  const [editing, setEditing] = useState(false);
  const [galleryOpen, setGalleryOpen] = useState(false);
  const [resetOpen, setResetOpen] = useState(false);
  const [focusId, setFocusId] = useState<string | null>(null);
  const editor = useDashboardEditor(entries);
  const leaveGuard = editing && editor.dirty;
  const blocker = useBlocker({
    shouldBlockFn: () => leaveGuard,
    disabled: !leaveGuard,
    enableBeforeUnload: leaveGuard,
    withResolver: true,
  });

  const startEditing = (): void => {
    editor.restart(entries);
    setFocusId(null);
    setEditing(true);
  };
  const cancelEditing = (): void => {
    editor.restart(entries);
    setEditing(false);
  };
  const saveEditing = (): void => {
    setEditing(false);
    layoutHandle.save(editor.tiles, {
      // 409/428 (másik eszközön közben mentettek): a friss elrendezés betöltődik, a draft elvész (a toast szól róla).
      // Minden más hibánál a draft megmarad, és a szerkesztő visszanyílik — a felhasználó újra menthet.
      onError: (err) => {
        const status = err instanceof EntityHttpError ? err.status : 0;
        if (status === 409 || status === 428) return;
        setEditing(true);
      },
    });
  };
  const resetToDefault = (): void => {
    setResetOpen(false);
    setEditing(false);
    layoutHandle.reset();
  };
  const addTile = (id: string): void => {
    editor.add(id);
    setFocusId(id);
  };
  // az Ünnepeltek ablaka a csempe saját beállítása: a látható elrendezés módosított másolata mentődik
  const setWindowDays = (days: DashboardWindowDays): void => {
    layoutHandle.save(
      entries.map((e) => (e.id === 'celebrations' ? { ...e, opts: { windowDays: days } } : e)),
    );
  };

  const tileCtx: TileContext = {
    data,
    counts: { clients: body?.clientCount ?? null, accounts: body?.accountCount ?? null },
    countsLoading: showCounts && dash.isLoading,
    currentUserId,
    onWindowDaysChange: setWindowDays,
  };

  const actions = newActions(can);
  const fabActions = newActionsFab(
    actions,
    (k) => t(`dashboard.newMenu.items.${k}`),
    (a) => void navigate({ to: a.to as never, search: a.search as never }),
  );

  const customizeDisabled = !layoutHandle.editable || layoutHandle.isLoading;
  const customizeButton = (
    <Button
      variant="outline"
      onClick={startEditing}
      disabled={customizeDisabled}
      aria-label={t('dashboard.editor.customize')}
    >
      <AdjustmentsHorizontalIcon aria-hidden />
      <span className="hidden sm:inline">{t('dashboard.editor.customize')}</span>
    </Button>
  );

  const toolbar = {
    dirty: editor.dirty,
    saving: layoutHandle.isSaving,
    onAdd: () => setGalleryOpen(true),
    onReset: () => setResetOpen(true),
    onCancel: cancelEditing,
    onSave: saveEditing,
  };

  return (
    <AppShell hideFab={editing} secondaryActions={editing ? undefined : fabActions}>
      <PageBody align="left" tight {...(editing ? { className: 'pb-28 md:pb-6' } : {})}>
        {/* (a) FEJLÉC — a köszöntés a lap címe (NINCS alcím: user-döntés); jobbra az „Új…" menü és a Testreszabás. */}
        <div className="mb-6 flex flex-wrap items-center justify-between gap-3">
          <h1 className="min-w-0 text-2xl font-semibold tracking-tight">{greeting}</h1>
          {editing ? (
            <EditorHeaderActions {...toolbar} />
          ) : (
            <div className="flex items-center gap-2">
              {!impersonating &&
                (customizeDisabled && !layoutHandle.isLoading ? (
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <span tabIndex={0}>{customizeButton}</span>
                    </TooltipTrigger>
                    <TooltipContent>{t('dashboard.editor.unavailable')}</TooltipContent>
                  </Tooltip>
                ) : (
                  customizeButton
                ))}
              <NewMenu actions={actions} />
            </div>
          )}
        </div>

        {editing ? (
          <div className="space-y-4">
            <p className="text-muted-foreground text-sm">{t('dashboard.editor.hint')}</p>
            <DashboardEditor
              tiles={editor.tiles}
              onMove={editor.move}
              onRemove={editor.remove}
              onResize={editor.resize}
              focusId={focusId}
            />
          </div>
        ) : (
          <div className="flex flex-col gap-4">
            {/* (b) FIX BLOKK — a „Rám vár" (teljes szélesség, nem mozgatható). A kitűzött közlemény KÖTELEZŐ csempe. */}
            {data.workItems.length > 0 && (
              <MyWorkCard items={data.workItems} loading={data.workLoading} />
            )}

            {/* (c) A TESTRESZABHATÓ csempe-terület. Betöltés alatt csontváz (a lap ne ugorjon az alapértelmezettről
                a mentettre). */}
            {layoutHandle.isLoading ? (
              <div className={DASHBOARD_GRID_CLASS}>
                <Skeleton className={cn('h-24 rounded-xl', tileSpanClass('small'))} />
                <Skeleton className={cn('h-24 rounded-xl', tileSpanClass('small'))} />
              </div>
            ) : (
              <DashboardTiles tiles={layout.tiles} ctx={tileCtx} />
            )}
          </div>
        )}
      </PageBody>

      {editing && <EditorBottomBar {...toolbar} />}

      <TileGallerySheet
        open={galleryOpen}
        onOpenChange={setGalleryOpen}
        placedIds={editor.tiles.map((x) => x.id)}
        can={can}
        catalogRev={layout.catalogRev}
        onAdd={addTile}
      />

      <ConfirmDialog
        open={resetOpen}
        onOpenChange={setResetOpen}
        tone="warning"
        title={t('dashboard.editor.resetTitle')}
        description={t('dashboard.editor.resetDescription')}
        confirmLabel={t('dashboard.editor.resetConfirm')}
        cancelLabel={t('dashboard.editor.cancel')}
        onConfirm={resetToDefault}
      />

      <ConfirmDialog
        open={blocker.status === 'blocked'}
        onOpenChange={(open) => {
          if (!open) blocker.reset?.();
        }}
        tone="warning"
        title={t('dashboard.editor.leaveTitle')}
        description={t('dashboard.editor.leaveDescription')}
        confirmLabel={t('dashboard.editor.leaveConfirm')}
        cancelLabel={t('dashboard.editor.leaveStay')}
        onConfirm={() => blocker.proceed?.()}
      />
    </AppShell>
  );
}
